import {
    Check,
    ChevronsUpDown,
    ChevronUp,
    Cloud,
    Command,
    RefreshCw,
    Search,
    User,
    TrendingDown,
    TrendingUp,
    ShieldCheck,
    ArrowUpRight,
    ArrowDownRight,
    Users as UsersIcon
} from "lucide-react";
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import { SiAmazon, SiGooglecloud } from "react-icons/si";
import { AWSAccount, CloudUsersPageProps } from "./interface";
import {
    awsAccountList,
    awsTenantAccountList,
    getAWSUsersList,
    getAzureUsersList,
    getGCPUsersList,
} from "./Data";

import { Button } from "../../ui/button";

import { Input } from "../../ui/input";
import { Card, CardContent } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { DetailsDialog } from "./DetailsDialog";
import TenantLists from "../TenantLists";
import CloudAccountList from "../CloudAccountList";
import { Link } from "react-router-dom";
import { myLocalStorage } from "../../StorageHelper";
import { VscAzure } from "react-icons/vsc";
import { FaGoogle } from "react-icons/fa6";
import TenantCloudResources from "../TenantCloud/TenantCloudResources";
import useUserStore from "../../../services/userStore";
import { triggerCloudSync } from "../../../triggerCloudSync";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import CloudFilters from "../CloudFilters";
import AlertTrendPanel from "./AlertTrendPanel";
import { AlertTriangle } from "lucide-react";
import "./CloudStatus.css";

const getActivityColor = (count: number) => {
    if (count < 10) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
    if (count <= 50) return "bg-gradient-to-r from-orange-400 to-orange-500";
    return "bg-gradient-to-r from-orange-600 to-orange-700";
};

const getActivityIntensity = (count: number) => {
    return Math.min(Math.max(Number(count) || 0, 0), 100);
};

function ActivityBar({ count, className = "" }: { count: number; className?: string }) {
    const intensity = getActivityIntensity(count);
    const colorClass = getActivityColor(count);

    return (
        <div className={`relative h-2 bg-muted rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${intensity}%` }}
            />
        </div>
    );
}

const CloudIcon = ({ provider }: { provider: string }) => {
    if (provider === "gcp") return <FaGoogle className="w-8 h-8 text-primary" />;
    if (provider === "aws") return <SiAmazon className="w-8 h-8 text-primary" />;
    return <VscAzure className="w-8 h-8 text-primary" />;
};

/** Filter riskTags: remove alert-count tags, and if both "X name" and "+X name" exist, only keep "+X name" */
const filterRiskTags = (tags: string[]): string[] => {
    // Step 1: remove alert count tags
    const noAlerts = tags.filter(t => !/\d+\s*alerts?$/i.test(t));
    // Step 2: deduplicate — if "+X name" exists, drop "X name"
    return noAlerts.filter(tag => {
        // If this tag starts with +, always keep it
        if (tag.startsWith("+")) return true;
        // Otherwise check if a "+<this tag>" version exists
        const plusVersion = `+${tag}`;
        return !noAlerts.includes(plusVersion);
    });
};


export default function Users({ cloudProvider }: CloudUsersPageProps) {
    const [selectedTenant, setSelectedTenant] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
    const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [alertCounts, setAlertCounts] = useState<Record<string, number>>({});
    const [securityProgress, setSecurityProgress] = useState<any>(null);
    const { email, role } = useUserStore((state) => state.user);
    const { syncStatus } = useCloudSyncStore((state) => ({
        syncStatus: state.syncStatus[cloudProvider],
    }));

    const fetchSecurityProgress = useCallback(async () => {
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || myLocalStorage.getItem("latestTenant");
            if (!tenantName) return;
            const cp = cloudProvider?.toLowerCase();
            let url = "";
            let params: any = { tenantName };
            if (cp === "gcp") {
                const projectId = myLocalStorage.getItem("gcpSelectedProjectId");
                if (!projectId) return;
                url = `${NGROK}/api/gcp/security-progress`;
                params.projectId = projectId;
            } else if (cp === "aws") {
                const accountId = myLocalStorage.getItem("selectedAwsAccount");
                if (!accountId) return;
                url = `${NGROK}/api/aws/security-progress`;
                params.accountId = accountId;
            } else if (cp === "azure") {
                const sub = myLocalStorage.getItem("latestSubList");
                const subscriptionId = sub?.azureSubscriptionId;
                if (!subscriptionId) return;
                url = `${NGROK}/api/azure/v1/security-progress`;
                params.subscriptionId = subscriptionId;
            }
            if (!url) return;
            const { data } = await axios.get(url, { params });
            // Store the users-specific sub-object for the Users page
            setSecurityProgress(data?.overallSecurityProgress?.users || null);
        } catch (err) {
            console.error("Error loading security progress:", err);
            setSecurityProgress(null);
        }
    }, [cloudProvider]);

    useEffect(() => {
        if (!cloudProviderChanges) return;

        const fetchUsers = async () => {
            setLoading(true);
            try {
                let data: any[] = [];
                if (cloudProvider === "gcp") {
                    data = await getGCPUsersList();
                } else if (cloudProvider === "aws") {
                    data = await getAWSUsersList();
                } else if (cloudProvider === "azure") {
                    data = await getAzureUsersList();
                }
                setUsers(data);
            } catch (err) {
                console.error("Error loading users:", err);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
        fetchSecurityProgress();
    }, [cloudProvider, selectedTenant, cloudProviderChanges, fetchSecurityProgress]);

    const toggleRoles = (userId: string) => {
        const newExpanded = new Set(expandedRoles);
        if (newExpanded.has(userId)) newExpanded.delete(userId);
        else newExpanded.add(userId);
        setExpandedRoles(newExpanded);
    };

    const handleSync = async () => {
        try {
            if (cloudProvider === "aws") {
                await triggerCloudSync(
                    "aws",
                    "/api/aws/ondemand",
                    selectedTenant
                );
            } else if (cloudProvider === "azure") {
                await triggerCloudSync(
                    "azure",
                    "/api/azure-sync/onDemand",
                    selectedTenant
                );
            } else if (cloudProvider === "gcp") {
                await triggerCloudSync(
                    "gcp",
                    "/api/gcp-sync/manual",
                    selectedTenant
                );
            }
            // Re-fetch users after sync instead of using sync response
            setLoading(true);
            try {
                let data: any[] = [];
                if (cloudProvider === "gcp") {
                    data = await getGCPUsersList();
                } else if (cloudProvider === "aws") {
                    data = await getAWSUsersList();
                } else if (cloudProvider === "azure") {
                    data = await getAzureUsersList();
                }
                setUsers(Array.isArray(data) ? data : []);
            } finally {
                setLoading(false);
            }
        } catch (err) {
            console.error("Error syncing users:", err);
        }
    };

    const handleTenantChange = (tenant: string) => {
        setSelectedTenant(tenant);
    };
    const handleCloudProvider = (value: any) => {
        setCloudProviderChanges(value);
    };
    const [riskFilter, setRiskFilter] = useState<"all" | "improved" | "worsened" | "neutral">("worsened");

    /** Map API riskChangeCategory to local status */
    const mapRiskCategory = (cat: string | null | undefined): "improved" | "worsened" | "neutral" => {
        switch (cat) {
            case "RISK_INCREASED": return "worsened";
            case "RISK_REDUCED": return "improved";
            case "NEUTRAL": return "neutral";
            default: return "worsened";
        }
    };

    const activeEntities = useMemo(() => {
        return users.map(u => ({
            key: u.email || u.id || u.uniqueId || Math.random().toString(),
            name: u.name || u.userName || (u.email ? u.email.split("@")[0] : "Unknown"),
            subtitle: u.email || "",
            roles: u.roles || [],
            activity: u.activity || 0,
            type: "user" as const,
            raw: u
        }));
    }, [users]);

    const analyzedEntities = useMemo(() => activeEntities.map(a => {
        const u = a.raw;
        const status = mapRiskCategory(u.riskChangeCategory);
        return {
            entity: a,
            status,
            alertCount: u.alertCount || 0,
            alertDelta: u.alertCountDelta || 0,
            highPrivilegeRoleCount: u.highPrivilegeRoleCount || 0,
            highPrivilegeRoleCountDelta: u.highPrivilegeRoleCountDelta || 0,
            riskTags: u.riskTags || [],
            highPrivAdded: u.highPrivilegeRoleCountDelta > 0 ? Array(u.highPrivilegeRoleCountDelta).fill("high-priv") : [],
            highPrivRemoved: u.highPrivilegeRoleCountDelta < 0 ? Array(Math.abs(u.highPrivilegeRoleCountDelta)).fill("high-priv") : [],
        } as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [activeEntities]);

    const improved = useMemo(() => analyzedEntities.filter(a => a.status === "improved"), [analyzedEntities]);
    const worsened = useMemo(() => analyzedEntities.filter(a => a.status === "worsened"), [analyzedEntities]);
    const neutral = useMemo(() => analyzedEntities.filter(a => a.status === "neutral"), [analyzedEntities]);

    // Use security-progress API users sub-object for the overall bar + hero cards
    const sp = securityProgress;
    const displayTotal = sp?.totalEntities ?? users.length;
    const displayImproved = sp?.riskReducedCount ?? improved.length;
    const displayWorsened = sp?.riskIncreasedCount ?? worsened.length;
    const displayNeutral = sp?.neutralCount ?? neutral.length;

    const improvePct = sp?.greenBarPercentage ?? (displayTotal ? (displayImproved / displayTotal) * 100 : 0);
    const worsenPct = sp?.redBarPercentage ?? (displayTotal ? (displayWorsened / displayTotal) * 100 : 0);
    const neutralPct = displayTotal ? Math.max(100 - improvePct - worsenPct, 0) : 0;

    const filteredAnalyzed = useMemo(() => {
        let list = analyzedEntities;
        if (riskFilter !== "all") {
            list = list.filter(a => a.status === riskFilter);
        }
        if (searchQuery) {
            const sq = searchQuery.toLowerCase();
            list = list.filter(a =>
                a.entity.name.toLowerCase().includes(sq) ||
                a.entity.subtitle.toLowerCase().includes(sq)
            );
        }
        return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyzedEntities, riskFilter, searchQuery]);

    const filteredUsers = useMemo(
        () => {
            if (!Array.isArray(users)) return [];
            return filteredAnalyzed;
        },
        [users, filteredAnalyzed]
    );

    const getAwsAccountName = async (): Promise<string> => {
        const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
        const response: AWSAccount[] = await awsTenantAccountList(email);

        const awsData = response.find((aws) => aws.accountId === selectedAwsAccount);

        return awsData?.accountName || "";
    };


    const getUserLink = (user: any) => {
        if (cloudProvider === "aws") {
            user["userPolicies"] = user.roles.map((role: any) => ({
                policyName: role
            }));
            user["username"] = user.name;
            user["userId"] = user.uniqueId;
            user["createDate"] = user.syncedAt;
            return {
                pathname: "/aws/users/userDetails",
                state: {
                    user,
                    groups: user.groups,
                    isconfigured: user.isAWSUserConfigured,
                    selectedAwsAccount: "",
                },
            };
        }

        if (cloudProvider === "azure") {
            console.log(user)
            user["azureUserId"] = user.uniqueId;
            user["displayName"] = user.name;
            user["userPrincipalName"] = user.principalName;
            return {
                pathname: "/azure-directory/azureusers/userDetails",
                state: {
                    user,
                    groups: user.groups,
                    roles: user.roles,
                    isconfigured: false,
                },
            };
        }

        if (cloudProvider === "gcp") {
            user["userName"] = user.name;
            user["projectId"] = myLocalStorage.getItem("gcpSelectedProjectId");
            return {
                pathname: "/gcp/gcpusers/userDetails",
                state: {
                    user,
                    groups: user.groups,
                    roles: user.roles,
                    isconfigured: false,
                },
            };
        }
        return { pathname: "#", state: {} };
    };

    if (role === "TENANT_USER") {
        return <TenantCloudResources cloudProvider={cloudProvider} />
    }


    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-6 " >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <CloudIcon provider={cloudProvider} />
                    <div className="flex-1">
                        <h1 className="text-2xl font-semibold text-foreground">
                            {cloudProvider.toUpperCase()} Users
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Manage cloud users and permissions
                        </p>
                    </div>
                </div>

                {/* Cloud Filters */}
                <CloudFilters
                    cloudProvider={cloudProvider}
                    onTenantChange={handleTenantChange}
                    onCloudAccountChange={handleCloudProvider}
                    isAdmin={role !== "TENANT_USER"}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search users…"
                    onSync={handleSync}
                    syncDisabled={syncStatus}
                    syncLoading={syncStatus}
                />

                {/* Users List */}
                {!loading && users.length > 0 && (
                    <div className="mb-6 space-y-6">
                        {/* Overall Security Progress */}
                        <div className="cs-progress-hero" style={{ margin: 0 }}>
                            <div className="cs-progress-details">
                                <div className="cs-progress-title-row">
                                    <h3 className="cs-progress-main-title">Overall Security Progress</h3>
                                    <span className="cs-progress-subtitle">{displayTotal} entities analyzed</span>
                                </div>

                                <div className="cs-bar-wrap">
                                    <div className="cs-bar-track">
                                        <div className="cs-bar-fill improved" style={{ width: `${improvePct}%` }} />
                                        <div className="cs-bar-fill worsened" style={{ width: `${worsenPct}%` }} />
                                        <div className="cs-bar-fill unchanged" style={{ width: `${neutralPct}%` }} />
                                    </div>
                                </div>

                                <div className="cs-bar-legend">
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot improved" />
                                        <TrendingDown className="cs-bar-legend-icon improved" />
                                        <span className="cs-bar-legend-count">{displayImproved}</span>
                                        <span className="cs-bar-legend-label">Risk Reduced</span>
                                    </div>
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot worsened" />
                                        <TrendingUp className="cs-bar-legend-icon worsened" />
                                        <span className="cs-bar-legend-count">{displayWorsened}</span>
                                        <span className="cs-bar-legend-label">Risk Increased</span>
                                    </div>
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot unchanged" />
                                        <ShieldCheck className="cs-bar-legend-icon unchanged" />
                                        <span className="cs-bar-legend-count">{displayNeutral}</span>
                                        <span className="cs-bar-legend-label">Neutral</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <AlertTrendPanel cloudProvider={cloudProvider} />

                        <div className="cs-hero" style={{ margin: 0, marginTop: "16px" }}>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon blue"><UsersIcon /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayTotal}</span>
                                    <span className="cs-hero-label">Total Users</span>
                                </div>
                            </div>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon green"><TrendingDown /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayImproved}</span>
                                    <span className="cs-hero-label">Risk Reduced</span>
                                </div>
                            </div>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon red"><TrendingUp /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayWorsened}</span>
                                    <span className="cs-hero-label">Risk Increased</span>
                                </div>
                            </div>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon gray"><ShieldCheck /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayNeutral}</span>
                                    <span className="cs-hero-label">Neutral</span>
                                </div>
                            </div>
                        </div>

                        {/* Status Filters */}
                        <div className="cs-tabs">
                            <button className={`cs-tab worsened-tab ${riskFilter === "worsened" ? "active" : ""}`} onClick={() => setRiskFilter("worsened")}>
                                <TrendingUp /> Risk Increased <span className="cs-tab-count">{worsened.length}</span>
                            </button>
                            <button className={`cs-tab improved-tab ${riskFilter === "improved" ? "active" : ""}`} onClick={() => setRiskFilter("improved")}>
                                <TrendingDown /> Risk Reduced <span className="cs-tab-count">{improved.length}</span>
                            </button>
                            <button className={`cs-tab unchanged-tab ${riskFilter === "neutral" ? "active" : ""}`} onClick={() => setRiskFilter("neutral")}>
                                <ShieldCheck /> Neutral <span className="cs-tab-count">{neutral.length}</span>
                            </button>
                        </div>
                    </div>
                )}

                {(loading || !cloudProviderChanges) && filteredUsers.length === 0 ? (
                    <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Card key={i} className="animate-pulse">
                                <CardContent className="p-4 flex items-center gap-4">
                                    <div className="w-8 h-8 rounded-full bg-muted/60 flex-shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-muted/60 rounded w-1/4" />
                                        <div className="h-3 bg-muted/60 rounded w-1/2" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                <CloudIcon provider={cloudProvider} />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} users found</h3>
                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                You currently don't have any user configure for this account.
                            </p>
                            {/**
                                      * <Button variant="outline" onClick={() => navigate("/aws/users")} data-testid="button-go-to-resources">
                                       Go to Resources
                                     </Button>
                                      */}

                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-2">
                        {filteredUsers.map((item) => {
                            const user = item.entity.raw;
                            user.roles = user.roles || [];
                            const analyzed = item;
                            const userLink = getUserLink(user);

                            const isExpanded = expandedRoles.has(user.id || user.email);
                            const visibleRoles = isExpanded ? user.roles : user.roles.slice(0, 2);
                            const hasMore = user.roles.length > 2;
                            return (

                                <Card key={item.entity.key} className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]">
                                    <CardContent className="p-4">
                                        <Link
                                            to={userLink.pathname}
                                            state={userLink.state}
                                            className="block hover:no-underline"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <User className="w-4 h-4 text-primary" />
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <h3 className="font-semibold text-sm text-foreground">
                                                            {user.name}
                                                        </h3>
                                                        <Badge
                                                            variant={user.isDormant ? "secondary" : "default"}
                                                            className={`text-xs h-5 ${user.isDormant
                                                                ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                                                : ""
                                                                }`}
                                                        >
                                                            {user.isDormant ? "Dormant" : "Active"}
                                                        </Badge>
                                                        {(analyzed?.alertCount > 0 || alertCounts[user.email] > 0) && (
                                                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40" title={`${analyzed?.alertCount || alertCounts[user.email]} alert${(analyzed?.alertCount || alertCounts[user.email]) > 1 ? 's' : ''}`}>
                                                                <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                                                                <span className="text-xs font-semibold text-red-600 dark:text-red-400">{analyzed?.alertCount || alertCounts[user.email]}</span>
                                                            </div>
                                                        )}
                                                        {analyzed && analyzed.riskTags?.length > 0 && (() => {
                                                            const tags = filterRiskTags(analyzed.riskTags);
                                                            return tags.length > 0 ? (
                                                                <div className="cs-card-tags ml-2 flex flex-wrap gap-1">
                                                                    {tags.map((tag: string, idx: number) => (
                                                                        <span key={idx} className="cs-card-tag added"><ArrowUpRight className="w-3 h-3" /> {tag}</span>
                                                                    ))}
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {user.email}
                                                    </p>
                                                </div>

                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    <div className="text-xs text-muted-foreground">Activity</div>
                                                    <ActivityBar count={user.activity} className="w-20" />
                                                </div>
                                            </div>
                                        </Link>
                                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-muted-foreground mb-1.5">Roles</div>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {visibleRoles.map((role: any, idx: any) => (
                                                        <Badge
                                                            key={idx}
                                                            variant={role === "Owner" ? "default" : "secondary"}
                                                            className="text-xs h-5"
                                                        >
                                                            {role}
                                                        </Badge>
                                                    ))}

                                                    {hasMore && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault(); // prevent navigation on toggle
                                                                toggleRoles(user.id || user.email);
                                                            }}
                                                            className="text-xs text-primary hover:underline font-medium flex items-center gap-0.5"
                                                        >
                                                            {isExpanded ? (
                                                                <>
                                                                    <ChevronUp className="w-3 h-3" />
                                                                    Less
                                                                </>
                                                            ) : (
                                                                <>+{user.roles.length - 2}</>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex-shrink-0 text-right">
                                                <div className="text-xs text-muted-foreground mb-1.5">
                                                    Last Activity
                                                </div>
                                                <div className="text-xs font-medium">
                                                    {user.lastActivityMessage}
                                                </div>
                                            </div>
                                        </div>


                                        <div className="mt-2 flex items-center justify-between">
                                            <div className="text-xs text-muted-foreground">
                                                Synced: {user.syncedAt}
                                            </div>
                                            <DetailsDialog data={user} title={`User: ${user.name}`} />
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}

                    </div>
                )}

            </div>
        </div>
    );
}
